<template>
  <div class="legal-page">
    <!-- Intro Section -->
    <PolicyIntroComp :content="introContent" />

    <!-- Payment Content Section -->
    <PolicyContentComp :content="contentData" />
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import WOW from 'wowjs'
import PolicyIntroComp from '@/components/PolicyIntroComp.vue'
import PolicyContentComp from '@/components/PolicyContentComp.vue'

export default {
  name: 'PaymentPolicyPage',
  components: {
    PolicyIntroComp,
    PolicyContentComp
  },
  setup() {
    onMounted(() => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()
    })

    // Intro content object
    const introContent = ref({
      sectionLabel: "Payment Terms",
      mainTitle: "Payment Policy",
      description: "This policy outlines our payment terms, collection methods, due dates, and procedures for handling payments and penalties."
    })

    // Payment content object
    const contentData = ref({
      sections: [
        {
          title: "1. Payment Methods",
          paragraphs: [
            {
              type: "text",
              content: "We accept the following payment methods:"
            },
            {
              type: "unordered",
              items: [
                "<strong>Credit/Debit Cards:</strong> Visa, MasterCard, American Express, Discover",
                "<strong>Bank Transfers:</strong> ACH, Wire transfers, SEPA (Europe)",
                "<strong>Digital Wallets:</strong> PayPal, Stripe, Apple Pay, Google Pay",
                "<strong>Cryptocurrency:</strong> Bitcoin, Ethereum (for select projects)",
                "<strong>Check Payments:</strong> For established clients (subject to approval)"
              ]
            }
          ]
        },
        {
          title: "2. Payment Terms",
          paragraphs: [
            {
              type: "text",
              content: "Our standard payment terms are as follows:"
            },
            {
              type: "unordered",
              items: [
                "<strong>New Clients:</strong> 50% upfront, 50% upon completion",
                "<strong>Established Clients:</strong> Net 30 days from invoice date",
                "<strong>Large Projects:</strong> Milestone-based payments (25%, 25%, 25%, 25%)",
                "<strong>Retainer Agreements:</strong> Monthly payments in advance",
                "<strong>Emergency Projects:</strong> 100% upfront payment required"
              ]
            }
          ]
        },
        {
          title: "3. Due Dates and Invoicing",
          paragraphs: [
            {
              type: "text",
              content: "Payment schedules and invoicing procedures:"
            },
            {
              type: "unordered",
              items: [
                "<strong>Invoice Generation:</strong> Invoices are generated upon project start or milestone completion",
                "<strong>Due Dates:</strong> Payment is due within 30 days of invoice date",
                "<strong>Payment Reminders:</strong> Automated reminders sent at 7, 14, and 21 days past due",
                "<strong>Final Notice:</strong> Final notice sent at 25 days past due",
                "<strong>Collection Process:</strong> Begins at 30 days past due"
              ]
            }
          ]
        },
        {
          title: "4. Late Payment Penalties",
          paragraphs: [
            {
              type: "text",
              content: "Penalties for late payments are as follows:"
            },
            {
              type: "unordered",
              items: [
                "<strong>1-15 days late:</strong> 1.5% monthly interest charge",
                "<strong>16-30 days late:</strong> 2.5% monthly interest charge",
                "<strong>31+ days late:</strong> 3.5% monthly interest charge + collection fees",
                "<strong>Collection Fees:</strong> $50 per collection attempt",
                "<strong>Legal Fees:</strong> Client responsible for all legal and collection costs"
              ]
            }
          ]
        },
        {
          title: "5. Currency and Exchange Rates",
          paragraphs: [
            {
              type: "text",
              content: "All prices are quoted in USD unless otherwise specified. For international clients:"
            },
            {
              type: "unordered",
              items: [
                "<strong>Exchange Rate:</strong> Current rate at time of payment processing",
                "<strong>Currency Conversion:</strong> Handled by payment processor",
                "<strong>International Fees:</strong> Client responsible for any bank transfer fees",
                "<strong>Tax Implications:</strong> Client responsible for local tax obligations"
              ]
            }
          ]
        },
        {
          title: "6. Refund and Chargeback Policy",
          paragraphs: [
            {
              type: "text",
              content: "Our refund and chargeback procedures:"
            },
            {
              type: "unordered",
              items: [
                "<strong>Refunds:</strong> Processed according to our refund policy",
                "<strong>Chargebacks:</strong> $50 processing fee for all chargebacks",
                "<strong>Disputes:</strong> We will provide documentation to support our case",
                "<strong>Resolution:</strong> 30-day resolution period for all disputes"
              ]
            }
          ]
        },
        {
          title: "7. Security and Privacy",
          paragraphs: [
            {
              type: "text",
              content: "We take payment security seriously:"
            },
            {
              type: "unordered",
              items: [
                "<strong>PCI Compliance:</strong> All card payments processed through PCI-compliant systems",
                "<strong>Encryption:</strong> All payment data encrypted in transit and at rest",
                "<strong>Data Storage:</strong> We do not store complete payment card information",
                "<strong>Fraud Protection:</strong> Advanced fraud detection and prevention systems"
              ]
            }
          ]
        },
        {
          title: "8. Contact Information",
          paragraphs: [
            {
              type: "text",
              content: "For payment-related questions or concerns:"
            }
          ],
          contactInfo: [
            { label: "Billing Department", value: "billing@sogdo.com" },
            { label: "Phone", value: "+1 (555) 123-4567" },
            { label: "Address", value: "123 Tech Street, Innovation District, San Francisco, CA 94105" }
          ]
        }
      ]
    })

    return {
      introContent,
      contentData
    }
  }
}
</script>